import React, { useEffect, useState, useCallback } from "react";
import { Gift, Copy, Check, Users, Wallet, Tag, ChevronDown, ChevronUp } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatRupiah } from "./ui";

export default function ReferralCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/referral/me");
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async (text, kind) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(kind);
    setTimeout(() => setCopied(null), 2000);
  };

  if (err) {
    return (
      <div className="card p-6">
        <h2 className="text-brand-800 mb-2">Ajak Teman, Dapat Reward</h2>
        <p className="text-sm text-red-600">{err}</p>
      </div>
    );
  }
  if (!data) return null;

  const isWorker = data.my_role === "WORKER";
  const benefit = isWorker
    ? `Komisi ${data.policy.worker_commission_pct}% dari nilai pesanan, setiap ${data.policy.worker_milestone} pekerjaan selesai milik orang yang kamu ajak.`
    : `Diskon biaya admin ${data.policy.customer_discount_pct}%, setiap ${data.policy.customer_milestone} transaksi selesai dari orang yang kamu ajak.`;
  const milestone = data.milestone;
  const progressInCycle = data.total_progress % milestone;
  const pendingDiscount = data.pending_fee_discount;
  const shareUrl = `${window.location.origin}/register?ref=${data.code}`;

  return (
    <div className="card p-6" data-testid="referral-card">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-brand-800 flex items-center gap-2">
          <Gift className="h-5 w-5 text-brand-600" /> Ajak Teman, Dapat Reward
        </h2>
        {pendingDiscount && (
          <span className="chip bg-success-50 text-success-600">
            <Tag className="h-4 w-4" /> Diskon admin {pendingDiscount.discount_pct}% siap
          </span>
        )}
      </div>
      <p className="text-sm text-brand-400 mt-1">{benefit}</p>

      {/* Kode + tombol salin */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-2xl border-2 border-dashed border-brand-200 bg-brand-50 px-4 py-3 flex items-center justify-between gap-2">
          <div>
            <p className="text-[11px] text-brand-400 font-semibold uppercase">Kode referral</p>
            <p className="font-mono text-xl font-bold tracking-widest text-brand-800" data-testid="referral-code">{data.code}</p>
          </div>
          <button type="button" onClick={() => copy(data.code, "code")}
            className="btn-ghost !px-3 !py-2 text-sm" data-testid="referral-copy-code">
            {copied === "code" ? <Check className="h-4 w-4 text-success-500" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
        <div className="rounded-2xl border-2 border-dashed border-brand-200 bg-brand-50 px-4 py-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] text-brand-400 font-semibold uppercase">Link undangan</p>
            <p className="text-sm text-brand-700 truncate">…/register?ref={data.code}</p>
          </div>
          <button type="button" onClick={() => copy(shareUrl, "link")}
            className="btn-ghost !px-3 !py-2 text-sm" data-testid="referral-copy-link">
            {copied === "link" ? <Check className="h-4 w-4 text-success-500" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Progres milestone */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-brand-600 font-semibold flex items-center gap-1">
            <Users className="h-4 w-4" /> {data.referred_count} orang diajak
          </span>
          <span className="text-brand-400">
            {data.next_reward_in
              ? `${data.next_reward_in} ${isWorker ? "pekerjaan" : "transaksi"} lagi ke reward berikutnya`
              : "Reward berikutnya menunggu progres"}
          </span>
        </div>
        <div className="mt-2 h-2.5 rounded-full bg-brand-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-brand-600 transition-all"
            style={{ width: `${Math.min(100, (progressInCycle / milestone) * 100)}%` }}
            data-testid="referral-progress-bar"
          />
        </div>
        <p className="text-xs text-brand-400 mt-1">
          Progres siklus ini: {progressInCycle}/{milestone} selesai
        </p>
      </div>

      {/* Ringkasan reward */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-brand-50 px-4 py-3">
          <p className="text-[11px] text-brand-400 font-semibold uppercase">Total reward</p>
          <p className="font-bold text-brand-800 text-lg" data-testid="referral-total-rewards">{data.total_rewards}x</p>
        </div>
        <div className="rounded-2xl bg-brand-50 px-4 py-3">
          <p className="text-[11px] text-brand-400 font-semibold uppercase">
            {isWorker ? "Komisi referral masuk" : "Reward aktif"}
          </p>
          <p className="font-bold text-brand-800 text-lg flex items-center gap-1" data-testid="referral-commission-total">
            {isWorker ? (
              <><Wallet className="h-4 w-4 text-success-500" /> {formatRupiah(data.commission_total)}</>
            ) : (
              <> {pendingDiscount ? `${pendingDiscount.count} diskon ${pendingDiscount.discount_pct}%` : "-"}</>
            )}
          </p>
        </div>
      </div>

      {/* Detail */}
      <button
        type="button"
        onClick={() => setShowDetail((s) => !s)}
        className="mt-4 text-sm text-brand-600 font-semibold flex items-center gap-1"
        data-testid="referral-toggle-detail"
      >
        {showDetail ? "Sembunyikan detail" : "Lihat detail"} {showDetail ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {showDetail && (
        <div className="mt-3 space-y-4">
          <div>
            <p className="text-sm font-bold text-brand-800 mb-2">Orang yang kamu ajak</p>
            {data.referred.length === 0 ? (
              <p className="text-sm text-brand-400">Belum ada. Bagikan kodemu dulu!</p>
            ) : (
              <div className="space-y-2" data-testid="referral-referred-list">
                {data.referred.map((r) => (
                  <div key={r.user_id} className="flex items-center justify-between rounded-xl border border-brand-100 px-3 py-2 text-sm">
                    <div>
                      <p className="font-semibold text-brand-800">{r.display_name || "Pengguna"}</p>
                      <p className="text-xs text-brand-400">{r.referred_role === "WORKER" ? "Pekerja" : "Pelanggan"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-brand-700">{r.completed_count} selesai</p>
                      <p className="text-xs text-success-600">{r.rewards_granted} reward</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-bold text-brand-800 mb-2">Riwayat reward</p>
            {data.rewards.length === 0 ? (
              <p className="text-sm text-brand-400">Belum ada reward. Semangat ajaknya!</p>
            ) : (
              <div className="space-y-2" data-testid="referral-rewards-list">
                {data.rewards.map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-xl border border-brand-100 px-3 py-2 text-sm">
                    <span className="text-brand-700">
                      {r.kind === "COMMISSION" ? `Komisi ${formatRupiah(r.amount)}` : `Diskon admin ${r.discount_pct}%`}
                    </span>
                    <span className={`chip ${r.status === "PAID" ? "bg-success-50 text-success-600" : r.status === "APPLIED" ? "bg-brand-100 text-brand-600" : "bg-amber-50 text-amber-600"}`}>
                      {r.status === "PAID" ? "Masuk dompet" : r.status === "APPLIED" ? "Terpakai" : "Siap dipakai"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
